import { AppText, AppIcon } from "@toothfairy/shared-ui";
import AppForms from "@toothfairy/shared-ui/AppForms";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppToast from "@toothfairy/shared-ui/AppToast";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { View, TouchableOpacity } from "react-native";
import ToothFairyButton from "../../../../../Components/ToothFairyComponents/ToothFairyButton";
import ToothFairyModal from "../../../../../Components/ToothFairyComponents/ToothFairyModal";
import LoadingPlaceholder from "../../../../../Components/LoadingPlaceholder";
import Constants from "../../../../../Constants";
import useWorkspaces from "../../../../../Hooks/useWorkspaces";

const FunctionHiringTemplateView = ({
  tool,
  availableAuthorisations,
  onSubmit,
  onCancel,
  isLoading,
}) => {
  const AppToasty = AppToast.useToast();
  const { currentTheme } = AppTheme.useTheme();
  const { getActiveWorkspace } = useWorkspaces();
  const { t } = useTranslation();

  const authItems = useMemo(() => {
    const items = [{ label: t("settings.list.selectAuthorisation"), value: null }];

    if (availableAuthorisations && availableAuthorisations.length > 0) {
      availableAuthorisations.forEach((auth) => {
        items.push({
          label: `${auth?.name} (${auth?.type || "Auth"})`,
          value: auth?.id,
        });
      });
    }

    return items;
  }, [availableAuthorisations]);

  const toolTypeIcon = AppAssets.icons.tools;

  return (
    <ToothFairyModal
      modalStyle={{
        height: "auto",
        minHeight: 400,
        maxWidth: 550,
        width: "90%",
        maxHeight: "90%",
        marginHorizontal: "auto",
        marginTop: "auto",
        marginBottom: "auto",
        margin: "auto",
      }}
      key={`hiring-${tool?.id}`}
      isModalOpen={true}
      setIsModalOpen={onCancel}
      modalTitle={`Configure ${tool?.name || "Tool"}`}
      closeModal={onCancel}
    >
      {isLoading ? (
        <LoadingPlaceholder caption={t("settings.list.loadingAuthorisations")} />
      ) : (
        <AppForms.AppForm
          initialValues={{ authorisation: null }}
          onSubmit={(values) => {
            if (!values?.authorisation) {
              AppToasty.show(t("settings.list.pleaseSelectAuthorisation"), {
                type: "danger",
              });
              return;
            }
            onSubmit({ authorisationID: values.authorisation });
          }}
        >
          <View style={{ padding: 20 }}>
            <View
              style={{
                paddingHorizontal: 15,
                paddingVertical: 12,
                backgroundColor: `${currentTheme?.theme?.primary}08`,
                borderRadius: 8,
                marginBottom: 20,
                borderLeftWidth: 3,
                borderLeftColor: currentTheme?.theme?.primary,
              }}
            >
              <AppText
                preventTranslation
                style={{ fontSize: 14, fontWeight: "600", marginBottom: 4 }}
                color={currentTheme?.theme?.white}
              >
                {t("settings.list.toolRequiresAuthorisation")}
              </AppText>
              <AppText
                preventTranslation
                style={{ fontSize: 12, lineHeight: 18 }}
                color={currentTheme?.theme?.white}
              >
                {t("settings.list.selectAuthorisationCaption")}
              </AppText>
            </View>

            <View style={{ marginBottom: 20 }}>
              <AppText
                style={{
                  fontSize: 12,
                  fontWeight: "600",
                  marginBottom: 8,
                }}
                color={currentTheme?.theme?.font_color}
              >
                {t("settings.list.toolDetails")}
              </AppText>
              <View
                style={{
                  padding: 12,
                  backgroundColor: currentTheme?.theme?.softBackgroundColor,
                  borderRadius: 6,
                  borderLeftWidth: 3,
                  borderLeftColor: currentTheme?.theme?.orange,
                }}
              >
                <View style={{ flexDirection: "row", alignItems: "center" }}>
                  <AppIcon
                    icon={toolTypeIcon}
                    size={16}
                    color={currentTheme?.theme?.orange}
                    style={{ marginRight: 8 }}
                  />
                  <AppText
                    style={{ fontSize: 14, fontWeight: "600", flex: 1 }}
                    color={currentTheme?.theme?.font_color}
                  >
                    {tool?.name}
                  </AppText>
                </View>
                {tool?.description && (
                  <AppText
                    style={{ fontSize: 11, marginTop: 6, marginLeft: 24 }}
                    color={currentTheme?.theme?.secondary_font_color}
                    numberOfLines={3}
                  >
                    {tool.description}
                  </AppText>
                )}
              </View>
            </View>

            <View style={{ marginBottom: 20 }}>
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <AppForms.AppFormDropDown
                  formFieldContainerStyle={{
                    flex: 1,
                  }}
                  isElevated={false}
                  dropDownStyle={{
                    borderColor: currentTheme?.theme?.border_color,
                  }}
                  showTextInputLabel
                  label={t("settings.list.selectAuthorisation")}
                  isMandatory={true}
                  name="authorisation"
                  items={authItems}
                />
                <View
                  style={{
                    flexDirection: "row",
                    gap: 10,
                    marginLeft: 10,
                    marginTop: 20,
                  }}
                >
                  <TouchableOpacity
                    onPress={() => {
                      window.open(
                        `/workspaces/${
                          getActiveWorkspace()?.id
                        }/settings/authorisations/`,
                        "_blank",
                      );
                    }}
                    style={{
                      padding: 8,
                      borderRadius: 6,
                      backgroundColor: currentTheme?.theme?.backgroundColor,
                      borderWidth: 1,
                      borderColor: currentTheme?.theme?.border_color,
                    }}
                  >
                    <AppIcon
                      icon={AppAssets?.icons?.add_icon}
                      size={18}
                      color={currentTheme?.theme?.primary}
                    />
                  </TouchableOpacity>
                </View>
              </View>

              {availableAuthorisations?.length === 0 && (
                <View
                  style={{
                    marginTop: 12,
                    padding: 12,
                    backgroundColor: `${currentTheme?.theme?.orange}15`,
                    borderRadius: 6,
                    borderLeftWidth: 3,
                    borderLeftColor: currentTheme?.theme?.orange,
                  }}
                >
                  <AppText
                    style={{ fontSize: 11 }}
                    color={currentTheme?.theme?.secondary_font_color}
                  >
                    {t("settings.list.noAuthorisationsFoundCaption")}
                  </AppText>
                </View>
              )}
            </View>

            <View
              style={{
                flexDirection: "row",
                justifyContent: "center",
                gap: 10,
              }}
            >
              <ToothFairyButton
                title={t("common.cancel")}
                onPress={onCancel}
                style={{
                  height: Constants.appButtonHeight,
                }}
                backgroundColor={currentTheme?.theme?.mediumGrey}
                fontColor={currentTheme?.theme?.white}
              />
              <AppForms.AppSubmitButton
                title={t("common.confirm")}
                backgroundColor={currentTheme?.theme?.primary}
                fontColor={currentTheme?.theme?.white}
                style={{
                  height: Constants.appButtonHeight,
                  width: 120,
                }}
              />
            </View>
          </View>
        </AppForms.AppForm>
      )}
    </ToothFairyModal>
  );
};

export default FunctionHiringTemplateView;
